import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PerformanceBreakdownTable, {
  type BreakdownRowSelection,
} from '../../components/analytics/PerformanceBreakdownTable';
import DrilldownPerformance from '../../components/analytics/DrilldownPerformance';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import analyticsService from '../../services/analyticsService';
import { useAnalyticsDataOptional } from '../../context/AnalyticsDataContext';
import type { AnalyticsFilters, PerBasketItem } from '../../types/analytics';
import type { BreakdownSelection } from '../../components/analytics/BreakdownLeaderboard';

const _OTHER_BASKET_ID = '__other__';

interface Props {
  /** Override filters. When omitted, falls back to AnalyticsData context
   *  (the analytics-route mount). Lets the Performance tab on BasketsPage
   *  reuse the same UI scoped to a single basket without standing up the
   *  full AnalyticsLayout. */
  filters?: AnalyticsFilters;
}

const BasketsSection: React.FC<Props> = ({ filters: filtersProp }) => {
  const ctx = useAnalyticsDataOptional();
  const filters = filtersProp ?? ctx?.filters;
  if (!filters) {
    throw new Error('BasketsSection requires filters prop or AnalyticsDataProvider context');
  }
  const [items, setItems] = useState<PerBasketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<BreakdownRowSelection | null>(null);

  const fetchPerBasket = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await analyticsService.getPerBasket(filters);
      setItems(r.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load breakdown.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchPerBasket(); }, [fetchPerBasket]);

  // Filter changes can invalidate the current selection (selected basket
  // may no longer exist). Clear when filters change to keep the drill-down
  // honest.
  useEffect(() => { setSelected(null); }, [filters]);

  // Backend /analytics/per-basket returns all baskets the user had activity
  // in — it doesn't honor `filters.baskets` / `filters.strategies`. Apply
  // them client-side so the Performance tab on BasketsPage (which pre-scopes
  // to one basket) renders just that basket and its strategies, and so the
  // analytics route's FilterBar selections actually narrow the table.
  const visibleItems = useMemo(() => {
    const bIds = filters.baskets ?? [];
    const sIds = filters.strategies ?? [];
    if (bIds.length === 0 && sIds.length === 0) return items;
    let rows = items;
    if (bIds.length > 0) {
      const allow = new Set(bIds);
      rows = rows.filter(b => allow.has(b.basketId));
    }
    if (sIds.length > 0) {
      const allow = new Set(sIds);
      rows = rows
        .map(b => ({ ...b, strategies: b.strategies.filter(s => allow.has(s.strategyId)) }))
        .filter(b => b.strategies.length > 0);
    }
    return rows;
  }, [items, filters.baskets, filters.strategies]);

  // Map the tree-table's selection shape onto DrilldownPerformance's existing
  // {dimension, key, name} contract.
  const drilldown: BreakdownSelection | null = (() => {
    if (!selected) return null;
    if (selected.kind === 'strategy') {
      return {
        dimension: 'strategy',
        key: selected.strategyId,
        name: selected.strategyName,
      };
    }
    // Basket drill-down. The synthetic "Other / Marketplace" parent has a
    // non-real basketId — skip drill-down for that row (it groups multiple
    // unrelated strategies; no single scope makes sense).
    if (selected.basketId === _OTHER_BASKET_ID) return null;
    return {
      dimension: 'basket',
      key: selected.basketId,
      name: selected.basketName,
    };
  })();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Performance Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <PerformanceBreakdownTable
            items={visibleItems}
            loading={loading}
            error={error}
            selected={selected}
            onSelect={setSelected}
          />
        </CardContent>
      </Card>

      {drilldown && (
        <DrilldownPerformance
          filters={filters}
          selection={drilldown}
          onClear={() => setSelected(null)}
        />
      )}
    </>
  );
};

export default BasketsSection;
